import {useEventsStore} from "@src/Store/eventsStore.ts";
import {CheckInOutPair} from "@src/httpClient/__generated__/Api.ts";
import {Stack} from "@src/CommonComponents/Stack/Stack.tsx";
import {LogOut} from "@UI/components/icons/LogOut.tsx";
import {
    Body_14_Coral_Empasis_14,
    Body_14_Coral_Primary_Light_14, Body_14_Coral_Primary_Regular_14, Body_14_Coral_Strong_emphasis_14,
    Body_16_Coral_Emphasis_16,
    Body_16_Coral_Primary_Regular_16,
    Body_16_Coral_Strong_emphasis_16
} from "@UI/typography/generated/typography.tsx";
import {Gt, GtDatetime} from "@src/i18n/Gt.tsx";
import {getTimeFromDT} from "@src/utils/formatDate.ts";
import {LogIn} from "@UI/components/icons/LogIn.tsx";
import {useMemo, useState} from "react";

import styles from "./eventsHistoryElement.module.less";
import {WorkdayWithEmployee} from "@src/httpClient/__generated__/Api.ts";
import cn from "classnames";
import {Button, ButtonType} from "@UI/components/controls/Button/Button.tsx";
import {MapPinWhite} from "@UI/components/icons/MapPinWhite.tsx";
import {ColorNames} from "@UI/theme/generated/ColorNames.ts";
import {Colors} from "@UI/theme/generated/Colors.ts";
import {MapPinOff} from "@UI/components/icons/MapPinOff.tsx";
import {Clock} from "@UI/components/icons/Clock.tsx";
import {Modal} from "@src/CommonComponents/Modal/Modal.tsx";
import {useCredentialsStore} from "@src/Store/userStore.ts";
import {getLocationLink, getLocationsString} from "@src/utils/coordLink.ts";
import { EventsHistoryGroup } from "@src/CommonComponents/EventsHistoryElement/EventsHistoryGroup";
import {WorkDay} from "@src/db.ts";

interface Props {
    events?: CheckInOutPair,
    workDay?: WorkdayWithEmployee | WorkDay,
    extended?: boolean
    date?: boolean
}

export const EventsHistoryElement = ({events, workDay, extended = false, date = true}: Props) => {
    const isStartInTime: boolean = useMemo(() => {
        if (!workDay?.start_time || !events?.event_in?.recorded_at) return true
        return getTimeFromDT(events.event_in.recorded_at) >= getTimeFromDT(workDay.start_time.toString())
    }, [workDay, events])

    const {inCoords, inLink} = useMemo(() => {
        if (!events?.event_in) return {
            inCoords: "—,—",
            inLink: undefined,
            inStatus: undefined
        };

        return {
            inCoords: getLocationsString(events?.event_in),
            inLink: events?.event_in?.latitude && events?.event_in?.longitude ? getLocationLink(events?.event_in) : undefined,
            inStatus: "localStatus" in events.event_in ? events.event_in.localStatus : undefined
        }
    }, [events])

    const {outCoords, outLink} = useMemo(() => {
        if (!events?.event_out) return {
            outCoords: "—,—",
            outLink: undefined,
            outStatus: undefined,
        };
        return {
            outCoords: getLocationsString(events?.event_out),
            outLink: events?.event_out?.latitude && events?.event_out?.longitude ? getLocationLink(events?.event_out) : undefined,
            outStatus: "localStatus" in events.event_out ? events.event_out.localStatus : undefined
        }
    }, [events])

    if (extended) return (  // Attendance history
        <Stack direction={"column"} gap={24} className={styles.extendedHistoryElem}>
            {date &&
                <Body_16_Coral_Primary_Regular_16>
                    <GtDatetime>{events?.date || ""}</GtDatetime>
                </Body_16_Coral_Primary_Regular_16>
            }

            <Stack alignItems={"center"} className={cn(styles.historySub)} gap={16}>
                <Stack direction={"column"} justifyContent={"center"} style={{width: "100%"}}>
                    <Body_16_Coral_Emphasis_16 style={{color: Colors.Obsidian_Dark_500}}>
                        <Gt>Start work</Gt>
                    </Body_16_Coral_Emphasis_16>
                    <Stack>
                        <Stack gap={4} className={styles.time} alignItems={"center"}>
                            <Clock size={18}/>
                            <Body_14_Coral_Primary_Regular_14>
                                {events?.event_in?.recorded_at
                                    ? getTimeFromDT(events?.event_in?.recorded_at)
                                    : <span>—:—</span>
                                }
                            </Body_14_Coral_Primary_Regular_14>
                        </Stack>

                        <Stack className={styles.coords}>
                            {events?.event_in?.recorded_at
                                ? inLink
                                    ?
                                    <Button type={ButtonType.link} icon={<MapPinWhite size={20}/>}
                                            className={styles.coordsButton}
                                            color={ColorNames.CORAL_INK}
                                            externalLink={true}
                                            href={inLink}>
                                        <Body_14_Coral_Empasis_14>
                                            {inCoords}
                                        </Body_14_Coral_Empasis_14>
                                    </Button>
                                    :
                                    <Stack alignItems={"center"} className={styles.errorTime} gap={10}>
                                        <MapPinOff color={Colors.Coral_special_Error_red}/>
                                        <Body_14_Coral_Strong_emphasis_14>
                                            <Gt>No location information</Gt>
                                        </Body_14_Coral_Strong_emphasis_14>
                                    </Stack>
                                :
                                <Stack className={styles.coordsPlaceholder} alignItems={"center"} gap={4}>
                                    <MapPinWhite size={20}/>
                                    —,—
                                </Stack>

                            }
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
            <Stack alignItems={"center"} className={cn(styles.historySub, styles.topBorder)} gap={16}>
                <Stack direction={"column"} justifyContent={"center"} style={{width: "100%"}}>
                    <Body_16_Coral_Emphasis_16 style={{color: Colors.Obsidian_Dark_500}}>
                        <Gt>Finish work</Gt>
                    </Body_16_Coral_Emphasis_16>
                    <Stack>
                        <Stack gap={4} className={styles.time} alignItems={"center"}>
                            <Clock size={18}/>
                            <Body_14_Coral_Primary_Regular_14>
                                {events?.event_out?.recorded_at
                                    ? getTimeFromDT(events?.event_out?.recorded_at)
                                    : <span>—:—</span>
                                }
                            </Body_14_Coral_Primary_Regular_14>
                        </Stack>

                        <Stack className={styles.coords}>
                            {events?.event_out?.recorded_at
                                ? outLink
                                    ?
                                    <Button type={ButtonType.link} icon={<MapPinWhite size={20}/>}
                                            className={styles.coordsButton}
                                            color={ColorNames.CORAL_INK}
                                            externalLink={true}
                                            href={outLink}>
                                        <Body_14_Coral_Empasis_14>
                                            {outCoords}
                                        </Body_14_Coral_Empasis_14>
                                    </Button>
                                    :
                                    <Stack alignItems={"center"} className={styles.errorTime} gap={10}>
                                        <MapPinOff color={Colors.Coral_special_Error_red}/>
                                        <Body_14_Coral_Strong_emphasis_14>
                                            <Gt>No location information</Gt>
                                        </Body_14_Coral_Strong_emphasis_14>
                                    </Stack>
                                :
                                <Stack className={styles.coordsPlaceholder} alignItems={"center"} gap={4}>
                                    <MapPinWhite size={20}/>
                                    —,—
                                </Stack>
                            }
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    )

    return (
        <Stack className={styles.historyElem}>
            <Stack alignItems={"center"} className={styles.historySub} gap={16}>
                <LogOut color={Colors.Coral_green_500}/>
                <Stack direction={"column"} justifyContent={"center"} className={styles.time}>
                    <Body_14_Coral_Primary_Light_14>
                        <Gt>Start work</Gt>
                    </Body_14_Coral_Primary_Light_14>
                    <div className={cn({[styles.errorTime]: !isStartInTime})} style={{minWidth: "35px"}}>
                        <Body_16_Coral_Strong_emphasis_16>
                            {events?.event_in?.recorded_at
                                ? getTimeFromDT(events?.event_in?.recorded_at)
                                : <span>—:—</span>
                            }
                        </Body_16_Coral_Strong_emphasis_16>
                    </div>
                </Stack>
            </Stack>
            <Stack alignItems={"center"} className={styles.historySub} gap={16}>
                <LogIn color={Colors.Coral_sky_600}/>
                <Stack direction={"column"} justifyContent={"center"} className={styles.time}>
                    <Body_14_Coral_Primary_Light_14>
                        <Gt>Finish work</Gt>
                    </Body_14_Coral_Primary_Light_14>

                    <div className={cn({[styles.errorTime]: !isStartInTime})} style={{minWidth: "35px"}}>
                        <Body_16_Coral_Strong_emphasis_16>
                            {events?.event_out?.recorded_at
                                ? getTimeFromDT(events?.event_out?.recorded_at)
                                : <span>—:—</span>
                            }
                        </Body_16_Coral_Strong_emphasis_16>
                    </div>

                </Stack>
            </Stack>
        </Stack>
    )
}


export const ExtendedEventsGroup = ({workDay, groupEvents}: {workDay?: WorkdayWithEmployee | WorkDay, groupEvents: CheckInOutPair[],}) => {
    const [showDetailModal, setShowDetailModal] = useState<boolean>(false)
    const employee = useCredentialsStore(x => x.employee)

    return(
         <>
            {showDetailModal && (
                <Modal onClose={() => setShowDetailModal(false)}>
                    <EventsHistoryGroup
                        eventsGroup={groupEvents}
                        workDay={workDay}
                        employee={employee!}
                    />
                </Modal>
            )}
            <Stack gap={16} className={styles.eventsBlockExtended} onClick={() => setShowDetailModal(true)}>
                {groupEvents?.map(e =>
                    <Stack className={cn(styles.eventsList, styles.extendedEvent)} direction={"column"}
                           key={`${e?.event_in?.id}-${e?.date}`}>
                        <EventsHistoryElement events={e} workDay={workDay} extended/>
                    </Stack>
                )}
            </Stack>
        </>
    )
}

export const EventsGroup =  ({workDay, groupEvents}: {workDay?: WorkdayWithEmployee | WorkDay, groupEvents: CheckInOutPair[],}) => {
    const [showDetailModal, setShowDetailModal] = useState<boolean>(false)
    const employee = useCredentialsStore(x => x.employee)

    return (
        <>
            {showDetailModal && (
                <Modal onClose={() => setShowDetailModal(false)}>
                    <EventsHistoryGroup
                        eventsGroup={groupEvents}
                        workDay={workDay}
                        employee={employee!}
                    />
                </Modal>
            )}
            <Stack className={styles.eventsList} direction={"column"} gap={16} onClick={() => setShowDetailModal(true)}>
                {groupEvents?.map(e =>
                    <EventsHistoryElement events={e} key={e?.date} workDay={workDay}/>
                )}
            </Stack>
        </>
    )
}

export const EventsList = ({workDay, extended = false}: {
    workDay?: WorkdayWithEmployee | WorkDay,
    extended?: boolean
}) => {
    const groupEvents = useEventsStore(x => x.groupedEvents)

    if (groupEvents?.length === 0) return (
        <Stack className={styles.eventsList} direction={"column"} alignItems={"flex-start"}>
            <EventsHistoryElement/>
        </Stack>
    )

    if (extended) return <ExtendedEventsGroup workDay={workDay} groupEvents={groupEvents} />
    return <EventsGroup workDay={workDay} groupEvents={groupEvents} />
}
